import { useAuth } from "@/features/auth/hooks/use-auth";
import { useAuthUserProfileStore } from "@/lib/stores/profile-store";
import { MAX_FREE_WORKS } from "@/lib/queries/works";
import { useUserInitialization } from "@/lib/hooks/use-user-initialization";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Logger from "@/lib/logger";

export interface UserProfileData {
  name: string;
  email: string;
  avatar: string;
  username: string;
  isPremium: boolean;
  worksCount: number;
  maxWorks: number;
}

/**
 * Enhanced user profile hook that uses cached initialization data
 * instead of making separate queries for profile, premium status, and works count
 */
export function useEnhancedUserProfile() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { profile } = useAuthUserProfileStore();

  // Get all data from our single initialization query - but only for authenticated users
  const { data: initData, isLoading } = useUserInitialization(user?.id || null);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
      toast.success("Signed out successfully");
    } catch (error) {
      Logger.error("Error signing out:", error);
      toast.error("Failed to sign out");
    }
  };

  // Use initialization data if available, otherwise fall back to store/defaults
  const profileData = initData?.profile || profile;
  const isPremium = initData?.premium_status?.isPremium || false;
  const worksCount = initData?.works_count || 0;

  // Create a standardized user data object that can be used across components
  const userData: UserProfileData =
    user && profileData
      ? {
          name: profileData.full_name || profileData.username || "User",
          email: user.email || "",
          avatar: profileData.avatar_url || "",
          username: profileData.username || "User",
          isPremium,
          worksCount,
          maxWorks: MAX_FREE_WORKS,
        }
      : {
          name: "",
          email: "Loading...",
          avatar: "",
          username: "Loading...",
          isPremium: false,
          worksCount: 0,
          maxWorks: MAX_FREE_WORKS,
        };

  return {
    user,
    profile: profileData,
    userData,
    isPremium,
    worksCount,
    isAuthenticated: !!user,
    // Only return loading state if user exists - prevents loading states for logged-out users
    isLoading: user ? isLoading : false,
    handleSignOut,
  };
}
